// src/components/Simulations.js
import React, { useState, useEffect, useRef } from "react";
import "../FileUpload/UploadFiles.css";

import Papa from "papaparse";

import { createQueue, deleteQueue, displayLog } from "../FileUpload/Queue";
import { simulateRun } from "../FileUpload/Simulate";
import LogDisplay from "../FileUpload/LogDisplay";
import { useNavigation } from "../../../../contexts/NavigationContext";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../../../components/Microgrid/BreadCrumbsFile";
import { ToastContainer ,toast } from "react-toastify";
import { sysConfigApi, deleteSysconfigDataset } from "../../../../services/api/sysconfigService";
import { PYTHON_API_BASE_URL } from "../../../../services/api/apiConstants";
import { runQueueName } from "../../utils/queueName";
import { formatElapsed, RUN_STATUS_MESSAGE } from "../../utils/runStatus";

// NEW IMPORT: Our History Import tab component


// Helper functions to parse and format dates from sysconfig
const parseSysconfigDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") return null;
  const [datePart, timePart] = dateStr.trim().split(" ");
  if (!datePart) return null;
  const [month, day, year] = datePart.split("/").map(Number);
  const [hours = 0, minutes = 0] = (timePart || "0:00").split(":").map(Number);
  const d = new Date(year, month - 1, day, hours, minutes);
  return isNaN(d.getTime()) ? null : d;
};

const toDatetimeLocalString = (date) => {
  if (!date) return null;
  const pad = (n) => n.toString().padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const extractDateRange = (config) => {
  let latestStart = null;
  let earliestEnd = null;

  const consider = (obj) => {
    if (!obj) return;
    const sd = parseSysconfigDate(obj.start_date);
    const ed = parseSysconfigDate(obj.end_date);
    if (sd && (!latestStart || sd > latestStart)) latestStart = sd;
    if (ed && (!earliestEnd || ed < earliestEnd)) earliestEnd = ed;
  };

  const meters = config?.Microgrid?.meters ?? [];
  for (const meter of meters) {
    consider(meter);
    for (const ev of meter.evs ?? []) consider(ev);
    for (const solar of meter.solar_arrays ?? []) consider(solar);
    for (const load of meter.uncontrollable_loads ?? []) consider(load);
  }

  return {
    latestStart: toDatetimeLocalString(latestStart),
    earliestEnd: toDatetimeLocalString(earliestEnd),
  };
};

const DEVICE_ARRAYS = [
  "evs", "batteries", "solar_arrays",
  "fueled_generators", "controllable_loads", "uncontrollable_loads",
];

// The run page takes the horizon in DAYS; the engine key demand_horizon_hours
// is in hours, so the request sends days * HOURS_PER_DAY. 7 days = 168 hours,
// the engine's maximum.
export const HOURS_PER_DAY = 24;
export const DEMAND_HORIZON_DAYS_DEFAULT = 1;
export const DEMAND_HORIZON_DAYS_MAX = 7;

/**
 * Validates the Simulation Horizon field, entered in days (sent to the
 * engine as demand_horizon_hours = days * HOURS_PER_DAY).
 * Returns an error string, or null when `value` is a whole number of days
 * in 0..7. Surrounding whitespace is tolerated (" 24 " passes); "", "24.5",
 * "1e2", "-1" and anything non-numeric are rejected.
 */
export const validateDemandHorizonDays = (value) => {
  const text = value === null || value === undefined ? "" : String(value).trim();
  if (text === "") {
    return "Simulation Horizon is required.";
  }
  if (!/^\d+$/.test(text)) {
    return "Simulation Horizon must be a whole number of days.";
  }
  if (Number(text) > DEMAND_HORIZON_DAYS_MAX) {
    return `Simulation Horizon must be between 0 and ${DEMAND_HORIZON_DAYS_MAX} days.`;
  }
  return null;
};

/**
 * Deep-clones a sysconfig and removes every device whose status is
 * "disconnected". Devices without a status field default to "connected"
 * and are kept.  Logs per-meter before/after counts whenever devices
 * are removed so the filtering is easy to verify in the console.
 */
function stripDisconnectedDevices(config) {
  const filtered = JSON.parse(JSON.stringify(config));
  const isConnected = (d) => (d?.status ?? "connected") === "connected";

  (filtered.Microgrid?.meters ?? []).forEach((meter, idx) => {
    DEVICE_ARRAYS.forEach((key) => {
      const before = (meter[key] ?? []).length;
      meter[key] = (meter[key] ?? []).filter(isConnected);
      const after = meter[key].length;
      if (before !== after) {
        console.log(
          `[stripDisconnectedDevices] meter[${idx}].${key}: ${before} -> ${after} (removed ${before - after} disconnected)`
        );
      }
    });
  });

  return filtered;
}

// The dataset name of a run's filtered sysconfig (see
// buildFilteredSimulationConfig): `<microgrid>-TEMP-<6 digits>-sysconfig.json`,
// with the same six digits as the run's queue. Each run writes, simulates and
// deletes only its own, so a run started while an earlier one on the same
// microgrid is still finishing doesn't lose its file to that run's cleanup.
// The storage manager takes dataset names of letters, digits, "_", "." and
// "-" ending in ".json", with no length limit; a container name keeps to that.
const runTempSysconfigName = (simulationName, sixDigits) =>
  `${simulationName}-TEMP-${sixDigits}-sysconfig.json`;

const Simulations = () => {
  // Add these ref declarations at the top of your component with other state declarations
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  // 1) NEW: State to track which tab is active
  const [activeTab, setActiveTab] = useState("generate");

  // --- Your existing states ---
  const [isDerLogExpanded, setIsDerLogExpanded] = useState(false);
  const [errorMessages, setErrorMessages] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({
    "Building Load": null,
    "Utility Tariff": null,
    "Solar Generation": null,
    "EV Availability": null,
  });
  const { setTimeSeriesFileName, selectedBranchId } = useNavigation();
  const [hasConnectedEVStorage, setHasConnectedEVStorage] = useState(false);
  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime] = useState("");
  const [startDateKey, setStartDateKey] = useState(0);
  const [endDateKey, setEndDateKey] = useState(0);
  const [demandHorizonDays, setDemandHorizonDays] = useState(DEMAND_HORIZON_DAYS_DEFAULT);
  const [customer, setCustomer] = useState(null);
  const token = sessionStorage.getItem("accessToken");
  const [log, setLog] = useState("");
  const [timeseriesFile, setTimeseriesFile] = useState("");
  const [economicSummaryFile, setEconomicSummaryFile] = useState("");
  const [dailysummaryFile, setDailysummaryFile] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [simulationInProgress, setSimulationInProgress] = useState(false);
  // When the current run started (Date.now() ms), or null when none is going,
  // and the whole seconds since, for the elapsed time under Start Simulation.
  const [runStartedAt, setRunStartedAt] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [simulationFailureMessage, setSimulationFailureMessage] = useState("");
  const [isDERRequired, setIsDERRequired] = useState(false);
  const [hasConnectedConsumption, setHasConnectedConsumption] = useState(false);
  const [missingDeviceTypes, setMissingDeviceTypes] = useState([]);
  const [isDataFetched, setIsDataFetched] = useState(false);
  
  // Actionable date range error state
  const [dateRangeError, setDateRangeError] = useState(null);
  const errorBannerRef = useRef(null);
  const simulateButtonRef = useRef(null);

  // Simulation results datasets.
  const [storageAccountName, setStorageAccountName] = useState("");
  const [customerSimulationName, setCustomerSimulationName] = useState("");
 
  // const rawSimulationName = sessionStorage.getItem("simulationName"); // full name from blob, if set
  const name = sessionStorage.getItem("microgridDesign");
  const simulationName = name;
  const datasetName = `${simulationName}-sysconfig.json`;

  const rawCustomerEmail = sessionStorage.getItem("selectedCustomerEmail") || "";
  const customerEmail = rawCustomerEmail.replace(/[@.]/g, "-");


  


  const accountname = sessionStorage.getItem("storageAccountName");
  const navigate = useNavigate();

  useEffect(() => {
    if (accountname && name) {
      fetchSimulationDateRange();
    }
  }, [accountname, name]);

  useEffect(() => {
    const email = sessionStorage.getItem("selectedCustomerEmail");
    if (email) {
      setCustomer(email);
      setCustomerSimulationName(simulationName);

      Promise.all([
        checkRequiredDevices(),
        fetchStorageAccountName(selectedBranchId),
      ])
        .then(() => {
          setIsDataFetched(true);
        })
        .catch((error) => {
          console.error("Error fetching required data:", error);
          setIsDataFetched(false);
        });
    } else {
      console.error("No customer email found in session storage.");
    }
  }, []);

  // Tick the elapsed time once a second while a run is going. It is worked
  // out from the start time rather than counted, so a background tab whose
  // timers the browser slowed down still shows the right time. The interval
  // stops when the run ends (runStartedAt goes back to null) and when the
  // page unmounts, even if the run itself carries on.
  useEffect(() => {
    if (runStartedAt === null) return undefined;
    const intervalId = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - runStartedAt) / 1000));
    }, 1000);
    return () => clearInterval(intervalId);
  }, [runStartedAt]);

  

  const fetchStorageAccountName = async (branchId) => {
    const API_URL = `${PYTHON_API_BASE_URL}/storage-account/?branch_id=${branchId}`;

    try {
      const response = await fetch(API_URL, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-User-ID": sessionStorage.getItem("UserID") || "",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch storage account name.");
      }

      const data = await response.json();

      if (data.storage_account) {
        setStorageAccountName(data.storage_account.storage_account_name);
      }
    } catch (error) {
      setStorageAccountName("");
    }
  };

  /**
   * Fetches the original sysconfig on page load to extract date ranges
   * for the start/end date inputs. Does NOT filter devices or POST anything.
   */
  const fetchSimulationDateRange = async () => {
    try {
      const config = await sysConfigApi({
        accountname: accountname,
        simulationName: simulationName,
        datasetName: datasetName,
        token: token,
        method: "GET",
        body: null,
      });

      const hasValidConfig =
        config?.Microgrid?.meters &&
        Array.isArray(config.Microgrid.meters) &&
        config.Microgrid.meters.length > 0;

      if (hasValidConfig) {
        const { latestStart, earliestEnd } = extractDateRange(config);
        if (latestStart) setStartDateTime(latestStart);
        if (earliestEnd) setEndDateTime(earliestEnd);
      }
    } catch (error) {
      console.error("[fetchSimulationDateRange] Error fetching sysconfig:", error);
    }
  };

  /**
   * Builds a temporary simulation sysconfig that excludes disconnected
   * devices. The original sysconfig is never overwritten — the filtered
   * copy is saved under this run's own TEMP dataset name
   * (runTempSysconfigName) so the simulation backend only sees connected
   * devices.
   *
   * Called from handleSimulate when the user clicks "Start Simulation", with
   * the run's six digits. Returns the temp dataset name so handleSimulate can
   * pass it directly to the simulation request and delete it afterwards.
   */
  const buildFilteredSimulationConfig = async (runSequence) => {
    const tempDatasetName = runTempSysconfigName(simulationName, runSequence);

    const config = await sysConfigApi({
      accountname: accountname,
      simulationName: simulationName,
      datasetName: datasetName,
      token: token,
      method: "GET",
      body: null,
    });

    const hasValidConfig =
      config?.Microgrid?.meters &&
      Array.isArray(config.Microgrid.meters) &&
      config.Microgrid.meters.length > 0;

    const baseSchema = hasValidConfig
      ? config
      : {
          Microgrid: {
            meters: [
              {
                name: "",
                make_and_model: "",
                serial_number: "",
                utility_tariff_csv: "",
                building_load: { historical_load_csv: "" },
                evs: [],
                batteries: [],
                solar_arrays: [],
                fueled_generators: [],
                controllable_loads: [],
                uncontrollable_loads: [],
              },
            ],
          },
        };

    const schema = stripDisconnectedDevices(baseSchema);

    // Sync building_load.historical_load_csv from the first *connected*
    // consumption device that has one, so the simulation backend finds the blob.
    if (schema.Microgrid?.meters) {
      schema.Microgrid.meters.forEach((meter) => {
        meter.building_load = meter.building_load || { historical_load_csv: "" };
        const loadWithCsv = (meter.uncontrollable_loads || []).find(
          (c) => c.historical_load_csv,
        );
        if (loadWithCsv?.historical_load_csv) {
          meter.building_load.historical_load_csv = loadWithCsv.historical_load_csv;
        }
      });
    }

    const schemaJson = JSON.stringify(schema, null, 2);

    console.log(
      `[buildFilteredSimulationConfig] Posting filtered sysconfig as "${tempDatasetName}" (original: "${datasetName}")`
    );

    await sysConfigApi({
      accountname: accountname,
      simulationName: simulationName,
      datasetName: tempDatasetName,
      token: token,
      method: "POST",
      body: schemaJson,
    });

    console.log(
      `[buildFilteredSimulationConfig] Temp sysconfig "${tempDatasetName}" saved successfully.`
    );

    return tempDatasetName;
  };

  const parseCSV = (csvText) => {
    const parsed = Papa.parse(csvText, { header: true });
    return parsed.data; // Return parsed data as an array of objects
  };

  

  
 

  const checkRequiredDevices = async () => {
    try {
      const config = await sysConfigApi({
        accountname: accountname,
        simulationName: simulationName,
        datasetName: datasetName,
        token: token,
        method: "GET",
        body: null,
      });

      const isConnected = (d) => (d?.status ?? "connected") === "connected";
      const meters = config?.Microgrid?.meters ?? [];

      const hasConsumption = meters.some((m) =>
        (m.uncontrollable_loads ?? []).some(isConnected),
      );
      const hasEVs = meters.some((m) =>
        (m.evs ?? []).some(isConnected),
      );
      const hasProduction = meters.some((m) => {
        const solarConnected = (m.solar_arrays ?? []).some(isConnected);
        const genConnected = (m.fueled_generators ?? []).some(isConnected);
        return solarConnected || genConnected;
      });
      const hasStorage = meters.some((m) => {
        const battConnected = (m.batteries ?? []).some(isConnected);
        const evConnected = (m.evs ?? []).some(isConnected);
        return battConnected || evConnected;
      });

      const missing = [];
      if (!hasConsumption) missing.push("consumption device");

      setHasConnectedEVStorage(hasEVs);
      setHasConnectedConsumption(hasConsumption);
      setIsDERRequired(hasProduction || hasStorage);
      setMissingDeviceTypes(missing);
    } catch (error) {
      console.error("Error fetching sysconfig:", error);
      setIsDERRequired(false);
      setHasConnectedEVStorage(false);
      setHasConnectedConsumption(false);
      setMissingDeviceTypes(["consumption device"]);
    }
  };

  
  const isValidDate = (dateString) => {
  if (!dateString) return false;

  const [datePart, timePart] = dateString.split("T");
  if (!datePart) return false;

  const [yyyy, mm, dd] = datePart.split("-").map(Number);
  const [hh = 0, min = 0] = (timePart?.split(":") || []).map(Number);

  const date = new Date(yyyy, mm - 1, dd, hh, min);

  // Check if JS rolled over the date
  return (
    date.getFullYear() === yyyy &&
    date.getMonth() + 1 === mm &&
    date.getDate() === dd &&
    date.getHours() === hh &&
    date.getMinutes() === min
  );
};

  // Check if time is on the hour (minutes === 0)
  const isOnTheHour = (dateTimeString) => {
    if (!dateTimeString) return true; // Let required validation handle empty
    const date = new Date(dateTimeString);
    return !isNaN(date) && date.getMinutes() === 0;
  };

  // Parse timeseries date range error from backend
  // Example: "For timeseries 'commercial-solar.csv', last value is at datetime '2023-01-07 23:00:00' which is less than simulation end '2023-05-07 01:00:00'."
  const parseTimeseriesError = (errorMessage) => {
    const regex = /For timeseries '([^']+)', (?:first|last) value is at datetime '(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})' which is (?:greater|less) than simulation (?:start|end) '(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})'/;
    const match = errorMessage.match(regex);
    
    if (match) {
      const csvName = match[1];
      const dataTimestamp = match[2];
      const simulationTimestamp = match[3];
      const isEndDateError = errorMessage.includes('simulation end');
      const isStartDateError = errorMessage.includes('simulation start');
      
      return {
        csvName,
        dataTimestamp,
        simulationTimestamp,
        isEndDateError,
        isStartDateError,
        // Convert to datetime-local format for input
        suggestedDateTime: dataTimestamp.replace(' ', 'T').substring(0, 16),
      };
    }
    return null;
  };

  // Format datetime for display (e.g., "Jan 7, 2023 11:00 PM")
  const formatDateTimeForDisplay = (dateTimeStr) => {
    const date = new Date(dateTimeStr.replace(' ', 'T'));
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Handle "Fix dates" action
  const handleFixDates = () => {
    if (!dateRangeError) return;
    
    const { suggestedDateTime, isEndDateError } = dateRangeError;
    
    if (isEndDateError) {
      setEndDateTime(suggestedDateTime);
      setEndDateKey((k) => k + 1);
      setErrorMessages((prev) => {
        const { endDateTime, ...rest } = prev;
        return rest;
      });
    } else {
      setStartDateTime(suggestedDateTime);
      setStartDateKey((k) => k + 1);
      setErrorMessages((prev) => {
        const { startDateTime, ...rest } = prev;
        return rest;
      });
    }
    
    // Clear the date range error banner
    setDateRangeError(null);
    
    // Focus the simulate button so user can press Enter to re-run
    setTimeout(() => {
      if (simulateButtonRef.current) {
        simulateButtonRef.current.focus();
      }
    }, 100);
    
    toast.success("Date updated. Ready to run simulation.", {
      position: "top-right",
      autoClose: 3000,
    });
  };

  // Dismiss the error banner
  const handleDismissError = () => {
    setDateRangeError(null);
  };

  // Handle keyboard events on error banner
  const handleBannerKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleDismissError();
    }
  };


  

  

  

  
  const validateInputs = () => {
    let errors = {};

    // Check Start Date
    
if (!startDateTime || startDateTime.trim() === "") {
      errors.startDateTime = "Start Date-Time is required.";
    } else if (!isValidDate(startDateTime)) {
      errors.startDateTime = "Invalid Start Date-Time (date does not exist).";
    } else if (!isOnTheHour(startDateTime)) {
      errors.startDateTime = "Time must be on the hour (minutes must be 00).";
    } else {
    // Check year range
    const startYear = new Date(startDateTime).getFullYear();
    if (startYear < 1900 || startYear > 2100) {
      errors.startDateTime = "Start Year must be between 1900 and 2100.";
    }
  }

    // Check End Date
    if (!endDateTime || endDateTime.trim() === "") {
      errors.endDateTime = "End Date-Time is required.";
    } else if (!isValidDate(endDateTime)) {
      errors.endDateTime = "Invalid End Date-Time (date does not exist).";
    } else if (!isOnTheHour(endDateTime)) {
      errors.endDateTime = "Time must be on the hour (minutes must be 00).";
    } else {

      const endYear = new Date(endDateTime).getFullYear();
    if (endYear < 1900 || endYear > 2100) {
      errors.endDateTime = "End Year must be between 1900 and 2100.";
    }
      // Only check relative order if both are valid
      const start = new Date(startDateTime);
      const end = new Date(endDateTime);
      if (!isNaN(start) && !isNaN(end) && end <= start) {
        errors.endDateTime =
          "End Date-Time must be later than Start Date-Time.";
      }
    }

    // Check Simulation Horizon
    const demandHorizonError = validateDemandHorizonDays(demandHorizonDays);
    if (demandHorizonError) {
      errors.demandHorizonDays = demandHorizonError;
    }

    setErrorMessages(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSimulate =async() => {
    const isValid = validateInputs();

    if (!isValid) {
      console.error("Simulation validation failed.");
      return;
    }
    setSimulationInProgress(true);
    setRunStartedAt(Date.now());
    setElapsedSeconds(0);
  let tempDatasetName = null;
  try{
    const customerName = accountname;
    const simulationName = customerSimulationName;

    if (!simulationName) {
      setErrorMessage("Simulation name is missing. Please re-select the microgrid design.");
      setSimulationInProgress(false);
      return;
    }

    // Six random digits for this run: they name both its queue and its temp
    // sysconfig.
    const randomSequence = Math.floor(100000 + Math.random() * 900000);

    // Build the filtered temp sysconfig (disconnected devices removed)
    tempDatasetName = await buildFilteredSimulationConfig(randomSequence);

    // Utility function to convert the date/time to the expected format.
    const formatDateTime = (dateTimeString) => {
      const date = new Date(dateTimeString);

      const pad = (n) => n.toString().padStart(2, "0");
      

      const month = pad(date.getMonth() + 1); // Months are 0-based
      const day = pad(date.getDate());
      const year = date.getFullYear(); // 4-digit year
      const hours = pad(date.getHours()); // 24-hour format
      const minutes = pad(date.getMinutes());
      
      return `${month}/${day}/${year} ${hours}:${minutes}`;
    }

    if (isNaN(Date.parse(startDateTime))) {
      // Update the error state for startDateTime
      setErrorMessages((prevErrors) => ({
        ...prevErrors,
        startDateTime: "The date time field is invalid.",
      }));
      return;
    }

    if (isNaN(Date.parse(endDateTime))) {
      // Update the error state for endDateTime
      setErrorMessages((prevErrors) => ({
        ...prevErrors,
        endDateTime: "The date time field is invalid.",
      }));
      return;
    }

    // The run's queue name, cut to fit Azure's 63-character limit.
    const reportChannelName = runQueueName(simulationName, randomSequence);

    // Prepare the request data.
    const requestData = {
      storage_account: customerName,
      microgrid_name: simulationName,

      der_sysconfig_json: tempDatasetName,

      start_date_time: formatDateTime(startDateTime),
      end_date_time: formatDateTime(endDateTime),
      // Entered in days on the page; the engine takes hours.
      demand_horizon_hours: Number(demandHorizonDays) * HOURS_PER_DAY,
     
      report_channel_name: reportChannelName,
    };

    const headers = {
      "x-input-storage-manager-type":
        process.env.REACT_APP_SIMULATE_STORAGE_INPUT_TYPE,
      "x-output-storage-manager-type":
        process.env.REACT_APP_SIMULATE_STORAGE_OUTPUT_TYPE,
      "x-report-channel-type": process.env.REACT_APP_REPORT_CHANNEL_TYPE,
      Authorization: `Bearer ${token}`,
      "X-User-ID": sessionStorage.getItem("UserID") || "",
    };

    // Clear globals and initialize queue info.
    setErrorMessage("");
    setLog("");
    setTimeseriesFile("");
    setEconomicSummaryFile("");
    setDailysummaryFile("");
    const queueInfo = {
      customerName: customerName,
      queueName: reportChannelName,
      accessToken: token,
    };

    // Create queue, start simulation, display simulation log.
    try {
      // Create the queue.
      
      await createQueue(queueInfo);

      // /simulate answers 202 as soon as the sim has taken the run, and the
      // sim then solves in a background thread, so it is the queue, not the
      // request, that says when the run is over. Start polling it before the
      // request so no line is missed. The sim writes a few lines before and
      // after the solve and none during it, so the log is quiet for most of
      // a run; the status and elapsed time under Start Simulation say so.
      // Polling ends on the first line that parses as a JSON object (the
      // final report), so any other line must stay plain text.
      const cancelToken = { cancelled: false };
      const logPromise = displayLog(
        queueInfo,
        setLog,
        setTimeseriesFile,
        setEconomicSummaryFile,
        setDailysummaryFile,
        setSimulationFailureMessage,
        { cancelToken },
      );

      try {
        await simulateRun(requestData, headers, setErrorMessage);
        // The sim accepted the run; wait for the log loop to read the final
        // message from the queue.
        await logPromise;
      } catch (runError) {
        // Stop the polling loop so it doesn't run forever when the simulate
        // request failed before a final message was queued, then surface the
        // original error.
        cancelToken.cancelled = true;
        await logPromise.catch(() => {});
        throw runError;
      }

      await deleteQueue(queueInfo);
      
      // Show success toast
      toast.success("Simulation completed successfully!", {
        position: "top-right",
        autoClose: 5000,
      });
      
    } catch (error) {
      setErrorMessage(error.message);
      
      // Try to parse timeseries date range error for actionable messaging
      const parsedError = parseTimeseriesError(error.message);
      
      if (parsedError) {
        // Set actionable error for inline banner display
        setDateRangeError({
          ...parsedError,
          displayDate: formatDateTimeForDisplay(parsedError.dataTimestamp),
        });
        
        // Add inline error to the date field
        const dateType = parsedError.isEndDateError ? 'endDateTime' : 'startDateTime';
        const displayDate = formatDateTimeForDisplay(parsedError.dataTimestamp);
        setErrorMessages((prev) => ({
          ...prev,
          [dateType]: `Must be on or ${parsedError.isEndDateError ? 'before' : 'after'} ${displayDate} (${parsedError.csvName}).`,
        }));
        
        // Scroll to banner and focus it
        setTimeout(() => {
          if (errorBannerRef.current) {
            errorBannerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            errorBannerRef.current.focus();
          }
        }, 100);
      } else {
        // Show generic error toast for non-date errors
        toast.error(`Simulation failed: ${error.message}`, {
          position: "top-right",
          autoClose: false,
        });
      }
      
      // Delete queue after error.
      try {
        await deleteQueue(queueInfo, setErrorMessage);
      } catch (deleteErr) {
        console.error(
          `handleSimulateClick:Error deleting queue '${queueInfo} error message: ${deleteErr.message}`,
        );
      }
    }
  }
  catch (error) {
    setErrorMessage(error.message);
    
    // Try to parse timeseries date range error for actionable messaging
    const parsedError = parseTimeseriesError(error.message);
    
    if (parsedError) {
      // Set actionable error for inline banner display
      setDateRangeError({
        ...parsedError,
        displayDate: formatDateTimeForDisplay(parsedError.dataTimestamp),
      });
      
      // Add inline error to the date field
      const dateType = parsedError.isEndDateError ? 'endDateTime' : 'startDateTime';
      const displayDate = formatDateTimeForDisplay(parsedError.dataTimestamp);
      setErrorMessages((prev) => ({
        ...prev,
        [dateType]: `Must be on or ${parsedError.isEndDateError ? 'before' : 'after'} ${displayDate} (${parsedError.csvName}).`,
      }));
      
      // Scroll to banner and focus it
      setTimeout(() => {
        if (errorBannerRef.current) {
          errorBannerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          errorBannerRef.current.focus();
        }
      }, 100);
    } else {
      // Show generic error toast for non-date errors
      toast.error(`Simulation error: ${error.message}`, {
        position: "top-right",
        autoClose: false,
      });
    }
  }
  finally {
    setSimulationInProgress(false);
    // Clears the status and elapsed time, and stops the ticking, however the
    // run ended.
    setRunStartedAt(null);

    // Only this run's own temp sysconfig: another run on the microgrid may
    // still be using its own.
    if (tempDatasetName) {
      try {
        await deleteSysconfigDataset({
          storageAccount: accountname,
          microgridName: simulationName,
          datasetName: tempDatasetName,
        });
        console.log(`[handleSimulate] Deleted temp sysconfig "${tempDatasetName}".`);
      } catch (e) {
        console.warn(`[handleSimulate] Failed to delete temp sysconfig "${tempDatasetName}":`, e);
      }
    }
  }
  }


  

  const handleStartDateTimeChange = (e) => {
    let value = e.target.value;

    // Extract year from the datetime value
    const yearMatch = value.match(/^\d{4}/);
    if (yearMatch && yearMatch[0].length > 4) {
      // If year has more than 4 digits, truncate it
      value = value.replace(/^\d+/, yearMatch[0].substring(0, 4));
    }

    // Validate the year is between 1900 and 2100
    const year = parseInt(value.substring(0, 4));
    if (!isNaN(year) && (year < 1900 || year > 2100)) {
      setErrorMessage("Year must be between 1900 and 2100");
      return;
    }

    setStartDateTime(value);
    
    // Clear date range error banner when user edits the start date
    if (dateRangeError && dateRangeError.isStartDateError) {
      setDateRangeError(null);
    }

    // Validate minutes are 00 (hourly constraint)
    if (value && value.length === 16) {
      const date = new Date(value);
      if (!isNaN(date) && date.getMinutes() !== 0) {
        setErrorMessages((prev) => ({
          ...prev,
          startDateTime: "Time must be on the hour (minutes must be 00).",
        }));
      } else {
        // Clear error if valid
        setErrorMessages((prev) => {
          const { startDateTime, ...rest } = prev;
          return rest;
        });
      }

    }
  };

  const handleEndDateTimeChange = (e) => {
    let value = e.target.value;

    // Extract year from the datetime value
    const yearMatch = value.match(/^\d{4}/);
    if (yearMatch && yearMatch[0].length > 4) {
      // If year has more than 4 digits, truncate it
      value = value.replace(/^\d+/, yearMatch[0].substring(0, 4));
    }

    // Validate the year is between 1900 and 2100
    const year = parseInt(value.substring(0, 4));
    if (!isNaN(year) && (year < 1900 || year > 2100)) {
      setErrorMessage("Year must be between 1900 and 2100");
      return;
    }

    setEndDateTime(value);
    
    // Clear date range error banner when user edits the end date
    if (dateRangeError && dateRangeError.isEndDateError) {
      setDateRangeError(null);
    }

    // Validate minutes are 00 (hourly constraint)
    if (value && value.length === 16) {
      const date = new Date(value);
      if (!isNaN(date) && date.getMinutes() !== 0) {
        setErrorMessages((prev) => ({
          ...prev,
          endDateTime: "Time must be on the hour (minutes must be 00).",
        }));
      } else {
        // Clear error if valid
        setErrorMessages((prev) => {
          const { endDateTime, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  const handleDemandHorizonDaysChange = (e) => {
    setDemandHorizonDays(e.target.value);

    // Clear any stale error; the value is re-validated on submit.
    setErrorMessages((prev) => {
      const { demandHorizonDays, ...rest } = prev;
      return rest;
    });
  };

  return (
    <div className="simulations">
      <h2 className="page-heading">Generate Simulation</h2>

      {!name && (
        <p style={{ margin: "1rem 0", color: "var(--text-secondary, #666)" }}>
          Select a microgrid to continue.
        </p>
      )}

      {name && (
        <>
      <Breadcrumbs
        crumbs={[
        
          {label: "Customer List",
      path: "/employees",          // navigation path
      state: { activeTab: "customers"}},
          {
            label: sessionStorage.getItem("customerFirstName"),
            path: `/microgrid-page-design`,
          },
          {
            label: name.replace(customerEmail + "-", "") + "-" + "Simulations",
            path: `/microgrid/meter/simulations`,
          },
          { label: "Generate Simulation" },
        ]}
      />

      {activeTab === "generate" && (
        <div className="main-grid">
          {/* Date Range Error Banner */}
          {dateRangeError && (
            <div
              ref={errorBannerRef}
              tabIndex={-1}
              onKeyDown={handleBannerKeyDown}
              style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderLeft: '4px solid #dc2626',
                borderRadius: '6px',
                padding: '16px 20px',
                marginBottom: '20px',
                outline: 'none',
              }}
              role="alert"
              aria-live="assertive"
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{
                    margin: '0 0 8px 0',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#991b1b',
                  }}>
                    Simulation dates exceed available data
                  </h4>
                  <p style={{
                    margin: '0 0 16px 0',
                    fontSize: '14px',
                    color: '#7f1d1d',
                    lineHeight: '1.5',
                  }}>
                    {dateRangeError.isEndDateError ? 'End date' : 'Start date'} is{' '}
                    {dateRangeError.isEndDateError ? 'after' : 'before'} the{' '}
                    {dateRangeError.isEndDateError ? 'last' : 'first'} data point for{' '}
                    <strong>{dateRangeError.csvName}</strong> ({dateRangeError.displayDate}).
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button
                      onClick={handleFixDates}
                      style={{
                        backgroundColor: '#16a34a',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#15803d'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#16a34a'}
                    >
                      Set {dateRangeError.isEndDateError ? 'end' : 'start'} date to {dateRangeError.displayDate}
                    </button>
                    <button
                      onClick={handleDismissError}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#991b1b',
                        cursor: 'pointer',
                        fontSize: '14px',
                        textDecoration: 'underline',
                        padding: '8px 4px',
                      }}
                    >
                      Dismiss
                    </button>
                  </div>
                  <p style={{
                    margin: '12px 0 0 0',
                    fontSize: '12px',
                    color: '#9ca3af',
                  }}>
                    Press Esc to dismiss, Tab to navigate
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Miscellaneous Input Parameters */}
          <div className="misc-input">
            <h3>Miscellaneous Input Parameters</h3>
            <form>
              <div className="form-row">
                <label className="form-item">
                  Start Date<span className="required">*</span>
                  <input
                    key={startDateKey}
                    type="datetime-local"
                    name="startDateTime"
                    ref={startDateRef}
                    defaultValue={startDateTime}
                    step="3600"
                    onChange={handleStartDateTimeChange}
                    min="1900-01-01T00:00"
                    max="2100-12-31T23:59"
                    style={dateRangeError && dateRangeError.isStartDateError ? {
                      borderColor: '#dc2626',
                      boxShadow: '0 0 0 1px #dc2626',
                    } : {}}
                  />
                  {errorMessages.startDateTime && (
                    <span className="error-message-uploadfiles">
                      {errorMessages.startDateTime}
                    </span>
                  )}
                </label>
                <label className="form-item">
                  End Date<span className="required">*</span>
                  <input
                    key={endDateKey}
                    type="datetime-local"
                    name="endDateTime"
                    ref={endDateRef}
                    defaultValue={endDateTime}
                    step="3600"
                    onChange={handleEndDateTimeChange}
                    min="1900-01-01T00:00"
                    max="2100-12-31T23:59"
                    style={dateRangeError && dateRangeError.isEndDateError ? {
                      borderColor: '#dc2626',
                      boxShadow: '0 0 0 1px #dc2626',
                    } : {}}
                  />
                  {errorMessages.endDateTime && (
                    <span className="error-message-uploadfiles">
                      {errorMessages.endDateTime}
                    </span>
                  )}
                </label>
                <label className="form-item">
                  Simulation Horizon (days)<span className="required">*</span>
                  <input
                    id="demandHorizonDays"
                    type="number"
                    name="demandHorizonDays"
                    min="0"
                    max={DEMAND_HORIZON_DAYS_MAX}
                    step="1"
                    value={demandHorizonDays}
                    onChange={handleDemandHorizonDaysChange}
                    aria-describedby="demandHorizonDaysHint"
                  />
                  <small
                    id="demandHorizonDaysHint"
                    style={{ fontWeight: "normal", color: "#6b7280", marginTop: "5px" }}
                  >
                    Days of look-ahead for sites with a demand charge and a battery or EVs. Each extra day roughly doubles run time. A large site takes ≈ 20 min per simulated year at 1 day, ≈ 1 h at 2, ≈ 7 h at 5; small sites take a few minutes. 0 disables look-ahead.
                  </small>
                  {errorMessages.demandHorizonDays && (
                    <span className="error-message-uploadfiles">
                      {errorMessages.demandHorizonDays}
                    </span>
                  )}
                </label>
              </div>
              <button
                ref={simulateButtonRef}
                className="simulate-button"
                onClick={handleSimulate}
                type="button"
                disabled={simulationInProgress || missingDeviceTypes.length > 0}
              >
                {simulationInProgress ? "Simulating..." : "Start Simulation"}
              </button>
              {runStartedAt !== null && (
                <p style={{ margin: "8px 0 0 0", fontSize: "14px", color: "#374151" }}>
                  Elapsed{" "}
                  <span role="timer" aria-label="Elapsed time" style={{ fontVariantNumeric: "tabular-nums" }}>
                    {formatElapsed(elapsedSeconds)}
                  </span>
                </p>
              )}
              {/* Always rendered, so screen readers announce the message when
                  a run starts. The timer is kept out of it: role="timer" is
                  not announced, where a live region would read every tick. */}
              <div role="status">
                {runStartedAt !== null && (
                  <p style={{ margin: "4px 0 0 0", fontSize: "14px", color: "#6b7280", lineHeight: 1.5 }}>
                    {RUN_STATUS_MESSAGE}
                  </p>
                )}
              </div>
              {missingDeviceTypes.length > 0 && (
                <p style={{ color: '#dc2626', fontSize: '14px', marginTop: '8px' }}>
                  Cannot simulate: missing {missingDeviceTypes.join(" and ")}.
                </p>
              )}
            </form>
          </div>

         
          <div className="der-log">
            <h3>
              Microgrid Simulation Log
              {dateRangeError && (
                <span style={{
                  fontSize: '12px',
                  fontWeight: 'normal',
                  color: '#6b7280',
                  marginLeft: '12px',
                }}>
                  (Error details shown above in friendly format)
                </span>
              )}
            </h3>
            <div
              className="log-output expanded"
              style={dateRangeError ? { opacity: 0.6 } : {}}
            >
              <LogDisplay log={log} />
              {/* This area will continue to get populated with new logs */}
            </div>
          </div>
          
          

          <div style={{ marginTop: "20px" }}>
            <button
              className="back-button"
              type="button"
              onClick={() =>
                navigate("/microgrid/meter/simulations", {
                  state: { fromMicrogrid: true },
                })
              }
            >
              Back to Simulations
            </button>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );

   
}

export default Simulations;
